/* HART · The HART Reserve
   1. The cart engine. The complete set is a Shopify bundle product ($150) whose three pieces come from the same
      inventory as the pieces sold on their own. Whenever a cart holds one T-shirt, one pair of shorts and one
      hoodie bought separately, they are swapped for one set, so the $25 saving is applied by Shopify's own price
      for the set, once per complete trio. Removing a piece from a set swaps it back to the other two pieces.
   2. The section: scroll reveals, Front and Back on the cards, adding a single piece, and the set builder.
   Everything works on server-rendered markup; without this script the pieces and the set still link to their
   product pages, where Shopify's own forms sell them. */

const CFG = (() => {
  try { return JSON.parse(document.getElementById('hart-reserve-config').textContent); } catch (e) { return null; }
})();
const SOURCE = 'hart-reserve';
const reduced = () => { try { return matchMedia('(prefers-reduced-motion: reduce)').matches; } catch (e) { return false; } };

/* ---------------------------------------------------------------- lookups */
const ORDER = (CFG && CFG.order) || ['tee', 'shorts', 'hoodie'];
const PIECE_OF = new Map();       // piece variant id -> { key, size }
const PIECE_VARIANT = new Map();  // "key|size" -> piece variant id
const SET_BY_SIZES = new Map();   // "teeSize|shortsSize|hoodieSize" -> set variant id
const SIZES_OF_SET = new Map();   // set variant id -> [teeSize, shortsSize, hoodieSize]
if (CFG) {
  ORDER.forEach((key) => {
    const p = CFG.pieces && CFG.pieces[key];
    if (!p) return;
    Object.entries(p.v || {}).forEach(([id, size]) => { PIECE_OF.set(String(id), { key, size }); PIECE_VARIANT.set(key + '|' + size, String(id)); });
  });
  Object.entries((CFG.set && CFG.set.v) || {}).forEach(([id, sizes]) => { SET_BY_SIZES.set(sizes.join('|'), String(id)); SIZES_OF_SET.set(String(id), sizes); });
}
const pieceTitle = (key) => (CFG && CFG.pieces[key] && CFG.pieces[key].title) || key;

/* ---------------------------------------------------------------- cart API */
const routes = () => (window.Theme && window.Theme.routes) || {};
const withJs = (u) => (u.endsWith('.js') ? u : u + '.js');
const URLS = {
  add: () => withJs(routes().cart_add_url || '/cart/add'),
  change: () => withJs(routes().cart_change_url || '/cart/change'),
  update: () => withJs(routes().cart_update_url || '/cart/update'),
  cart: () => withJs(routes().cart_url || '/cart'),
};

async function post(url, body) {
  const res = await fetch(url, { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify(body) });
  let data = null;
  try { data = await res.json(); } catch (e) { /* no body */ }
  if (!res.ok || (data && data.status && data.status >= 400)) {
    const err = new Error((data && (data.description || data.message)) || 'Your bag could not be updated.');
    err.data = data; throw err;
  }
  return data;
}
async function getCart() {
  const res = await fetch(URLS.cart(), { headers: { Accept: 'application/json' }, credentials: 'same-origin' });
  if (!res.ok) throw new Error('Your bag could not be read.');
  return res.json();
}

let eventsModule;
async function events() {
  if (eventsModule !== undefined) return eventsModule;
  try { eventsModule = await import('@shopify/events'); } catch (e) { eventsModule = null; }
  return eventsModule;
}

/* Tell the theme's cart drawer, cart page and bag count about a change, the way Horizon's own forms do. */
async function announce(action, work, target, lines) {
  const ev = await events();
  let deferred = null;
  if (ev && ev.CartLinesUpdateEvent && ev.CartLinesUpdateEvent.createPromise) {
    try {
      deferred = ev.CartLinesUpdateEvent.createPromise();
      (target || document.body).dispatchEvent(new ev.CartLinesUpdateEvent({ action, context: action === 'add' ? 'product' : 'cart', lines: lines || [], promise: deferred.promise }));
    } catch (e) { deferred = null; }
  }
  const finish = (cart, didError) => {
    if (deferred) deferred.resolve({ cart: ev.CartLinesUpdateEvent.createCartFromAjaxResponse(cart), detail: { items: cart.items, itemCount: cart.item_count, source: SOURCE, didError } });
    else refreshCartSections();
  };
  try {
    const cart = await work();
    finish(cart, false);
    return cart;
  } catch (err) {
    try { finish(await getCart(), true); } catch (e) { if (deferred) deferred.reject(err); }
    throw err;
  }
}
async function refreshCartSections() {
  try {
    const { sectionRenderer } = await import('@theme/section-renderer');
    document.querySelectorAll('cart-items-component[data-section-id]').forEach((el) => {
      sectionRenderer.renderSection(el.dataset.sectionId, { cache: false, mode: el.dataset.drawer !== undefined ? 'hydration' : 'full' });
    });
  } catch (e) { if (location.pathname.replace(/\/$/, '').endsWith('/cart')) location.reload(); }
}

/* ---------------------------------------------------------------- the trio rule */
/* Pure: from a cart, the changes that turn every complete trio of separately-bought pieces into one set. */
export function planMerge(cart) {
  if (!CFG || !SET_BY_SIZES.size) return null;
  const units = {};
  ORDER.forEach((k) => { units[k] = []; });
  (cart.items || []).forEach((it) => {
    const p = PIECE_OF.get(String(it.variant_id));
    if (!p || it.selling_plan_allocation) return;
    for (let i = 0; i < it.quantity; i++) units[p.key].push({ key: it.key, size: p.size, variant: it.variant_id, properties: it.properties || {} });
  });
  const n = Math.min.apply(null, ORDER.map((k) => units[k].length));
  if (!n) return null;
  const used = {};      // line key -> units taken from it
  const lineQty = {};   // line key -> current quantity
  (cart.items || []).forEach((it) => { lineQty[it.key] = it.quantity; });
  const sets = {};      // set variant id -> count
  const restore = [];
  for (let i = 0; i < n; i++) {
    const trio = ORDER.map((k) => units[k][i]);
    const setId = SET_BY_SIZES.get(trio.map((u) => u.size).join('|'));
    if (!setId) continue;
    sets[setId] = (sets[setId] || 0) + 1;
    trio.forEach((u) => { used[u.key] = (used[u.key] || 0) + 1; restore.push({ id: u.variant, quantity: 1, properties: u.properties }); });
  }
  if (!Object.keys(sets).length) return null;
  const updates = {};
  Object.keys(used).forEach((k) => { updates[k] = lineQty[k] - used[k]; });
  return { updates, adds: Object.entries(sets).map(([id, quantity]) => ({ id: Number(id), quantity })), restore, trios: n };
}

async function applyMerge(plan) {
  await post(URLS.update(), { updates: plan.updates });
  try {
    await post(URLS.add(), { items: plan.adds });
  } catch (err) {
    try { await post(URLS.add(), { items: plan.restore }); } catch (e) { /* the pieces stay out; the error below explains */ }
    throw err;
  }
}

let busy = false;
let again = false;
async function reconcile() {
  if (!CFG) return;
  if (busy) { again = true; return; }
  busy = true;
  try {
    const cart = await getCart();
    const plan = planMerge(cart);
    if (plan) {
      await announce('update', async () => { await applyMerge(plan); return getCart(); }, document.body,
        Object.entries(plan.updates).map(([id, quantity]) => ({ id, quantity })));
    }
  } catch (e) {
    console.warn('[hart-reserve] could not apply the set price', e);
  } finally {
    busy = false;
    if (again) { again = false; reconcile(); }
  }
}
let timer = 0;
const schedule = () => { clearTimeout(timer); timer = setTimeout(reconcile, 350); };

/* Add items (from a card or the set builder), then apply the set rule, as one change the drawer shows. */
async function addItems(items, target) {
  return announce('add', async () => {
    await post(URLS.add(), { items });
    const plan = planMerge(await getCart());
    if (plan) await applyMerge(plan);
    return getCart();
  }, target, items.map((x) => ({ merchandiseId: String(x.id), quantity: x.quantity })));
}

/* Remove one piece from a set in the bag: the other two stay, each at its own price. */
async function splitSet(lineKey, setVariantId, qty, dropKey, target) {
  const sizes = SIZES_OF_SET.get(String(setVariantId));
  if (!sizes) throw new Error('That set could not be changed.');
  const keep = ORDER.map((k, i) => (k === dropKey ? null : PIECE_VARIANT.get(k + '|' + sizes[i]))).filter(Boolean).map((id) => ({ id: Number(id), quantity: 1 }));
  return announce('update', async () => {
    await post(URLS.change(), { id: lineKey, quantity: Math.max(0, qty - 1) });
    try { await post(URLS.add(), { items: keep }); }
    catch (err) { try { await post(URLS.add(), { items: [{ id: Number(setVariantId), quantity: 1 }] }); } catch (e) { /* restored as far as possible */ } throw err; }
    return getCart();
  }, target, [{ id: lineKey, quantity: Math.max(0, qty - 1) }]);
}

async function watchCart() {
  if (!CFG) return;
  const ev = await events();
  if (ev && ev.StandardEvents && ev.StandardEvents.cartLinesUpdate) {
    document.addEventListener(ev.StandardEvents.cartLinesUpdate, (e) => {
      if (!e.promise) { schedule(); return; }
      e.promise.then((r) => { const d = r && r.detail; if (d && (d.source === SOURCE || d.didError)) return; schedule(); }).catch(() => {});
    });
  }
  if (CFG.needs) reconcile();
}

/* the "remove one piece" buttons on a set in the bag (drawer or cart page; they re-render, so delegate) */
document.addEventListener('click', async (e) => {
  const btn = e.target.closest && e.target.closest('[data-hrs-split]');
  if (!btn) return;
  e.preventDefault();
  if (btn.getAttribute('aria-busy') === 'true') return;
  const box = btn.closest('[data-hrs-line]');
  const msg = box && box.querySelector('[data-hrs-line-msg]');
  btn.setAttribute('aria-busy', 'true');
  try {
    await splitSet(btn.dataset.hrsSplit, btn.dataset.setVariant, Number(btn.dataset.qty || 1), btn.dataset.drop, btn);
  } catch (err) {
    if (msg) { msg.textContent = err.message || 'That piece could not be removed. Try again.'; msg.hidden = false; }
  } finally { btn.removeAttribute('aria-busy'); }
});

/* ---------------------------------------------------------------- the section */
function revealInit(root) {
  document.documentElement.classList.add('hrs-js');
  const items = root.querySelectorAll('[data-hrs-reveal]');
  const lit = () => root.classList.add('is-lit');
  if (!('IntersectionObserver' in window) || reduced()) { items.forEach((el) => el.classList.add('is-in')); lit(); return; }
  const io = new IntersectionObserver((entries) => {
    entries.forEach((en) => { if (en.isIntersecting) { en.target.classList.add('is-in'); io.unobserve(en.target); } });
  }, { threshold: 0.12, rootMargin: '0px 0px -6% 0px' });
  items.forEach((el) => io.observe(el));
  const lio = new IntersectionObserver((entries) => { if (entries.some((en) => en.isIntersecting)) { lit(); lio.disconnect(); } }, { threshold: 0.2 });
  lio.observe(root);
  setTimeout(() => items.forEach((el) => el.classList.add('is-in')), 4000);   // never leave anything hidden
}

function messages(form) {
  const err = form.querySelector('[data-hrs-err]');
  const ok = form.querySelector('[data-hrs-ok]');
  return {
    fail(t) { if (ok) ok.hidden = true; if (err) { err.textContent = t; err.hidden = false; } },
    done(t) { if (err) err.hidden = true; if (ok) { ok.textContent = t; ok.hidden = false; setTimeout(() => { ok.hidden = true; }, 4000); } },
    clear() { if (err) err.hidden = true; if (ok) ok.hidden = true; },
  };
}
function busyBtn(btn, on) {
  if (!btn) return;
  if (on) { btn.setAttribute('aria-busy', 'true'); btn.disabled = true; } else { btn.removeAttribute('aria-busy'); btn.disabled = btn.dataset.locked === 'true'; }
}

/* a single piece, from its card */
function cardInit(card) {
  card.addEventListener('click', (e) => {
    const b = e.target.closest('[data-hrs-side]');
    if (!b) return;
    const side = b.dataset.hrsSide;
    card.dataset.side = side;
    card.querySelectorAll('[data-hrs-side]').forEach((x) => x.setAttribute('aria-pressed', String(x.dataset.hrsSide === side)));
  });
  const form = card.querySelector('[data-hrs-piece-form]');
  if (!form) return;
  const status = form.querySelector('[data-hrs-status]');
  const m = messages(form);
  form.addEventListener('change', (e) => {
    if (!e.target.matches('input[type="radio"]')) return;
    m.clear();
    if (status) { status.textContent = e.target.dataset.note || ''; status.className = 'hrs-status' + (e.target.dataset.low ? ' is-low' : ''); }
  });
  form.addEventListener('submit', async (e) => {
    e.preventDefault();
    const btn = form.querySelector('[type="submit"]');
    const picked = form.querySelector('input[type="radio"]:checked');
    if (!picked) {
      m.fail('Choose Men’s Medium or Men’s Large first.');
      const first = form.querySelector('input[type="radio"]:not(:disabled)');
      if (first) first.focus();
      return;
    }
    busyBtn(btn, true);
    try {
      await addItems([{ id: Number(picked.value), quantity: 1 }], form);
      m.done('Added to your bag.');
    } catch (err) {
      m.fail(err.message || 'This piece could not be added. Try again.');
    } finally { busyBtn(btn, false); }
  });
}

/* the complete set */
function setInit(root) {
  let data = null;
  try { data = JSON.parse(root.querySelector('[data-hrs-set-data]').textContent); } catch (e) { return; }
  // data: { pieces: [{ key, title, handle, variants: [{ id, size, available }] }], set: { handle, variants: [{ id, sizes: [...], available }] } }
  const state = {};
  data.pieces.forEach((p) => { state[p.key] = null; });
  const btn = root.querySelector('[data-hrs-set-add]');
  const m = messages(root);
  const rowOf = (key) => root.querySelector('[data-hrs-row="' + key + '"]');
  const pieceOf = (key) => data.pieces.find((p) => p.key === key);
  const variantOf = (key, size) => (pieceOf(key).variants || []).find((v) => v.size === size);
  const setVariantFor = () => data.set.variants.find((v) => data.pieces.every((p, i) => v.sizes[i] === state[p.key]));

  function rowStatus(key, text, cls) {
    const el = rowOf(key) && rowOf(key).querySelector('[data-hrs-status]');
    if (el) { el.textContent = text; el.className = 'hrs-status' + (cls ? ' ' + cls : ''); }
  }
  function sync() {
    data.pieces.forEach((p) => {
      const row = rowOf(p.key);
      if (!row) return;
      const anyAvail = p.variants.some((v) => v.available);
      row.querySelectorAll('input[type="radio"]').forEach((r) => {
        const v = variantOf(p.key, r.dataset.size);
        const out = !(v && v.available);
        r.disabled = out;
        if (out && r.checked) { r.checked = false; state[p.key] = null; }
      });
      row.classList.remove('is-need');
      if (!anyAvail) rowStatus(p.key, 'Sold out in both sizes', 'is-out');
      else if (!state[p.key]) rowStatus(p.key, 'Choose a size', '');
      else {
        const picked = row.querySelector('input[type="radio"]:checked');
        const note = picked && picked.dataset.note;
        rowStatus(p.key, state[p.key] + (note ? ' · ' + note : ''), picked && picked.dataset.low ? 'is-low' : '');
      }
    });
    const blocked = data.pieces.filter((p) => !p.variants.some((v) => v.available));
    if (btn) {
      btn.dataset.locked = blocked.length ? 'true' : 'false';
      btn.disabled = !!blocked.length;
    }
    const why = root.querySelector('[data-hrs-blocked]');
    if (why) {
      if (blocked.length === data.pieces.length) {
        why.textContent = 'All three pieces are sold out right now, so the complete set isn’t available.';
        why.hidden = false;
      } else if (blocked.length) {
        why.textContent = 'The ' + blocked.map((p) => p.title).join(' and the ') + (blocked.length > 1 ? ' are' : ' is') + ' sold out, so the complete set can’t be bought right now. ' + (blocked.length > 1 ? 'The other piece is still sold on its own.' : 'The other two pieces are still sold on their own.');
        why.hidden = false;
      } else why.hidden = true;
    }
  }

  // a size passed in the address (?tee=<variant id>) from a product page's "complete the set" link
  try {
    const q = new URLSearchParams(location.search);
    data.pieces.forEach((p) => {
      const id = q.get(p.key);
      const v = id && p.variants.find((x) => String(x.id) === id && x.available);
      if (v) { state[p.key] = v.size; const r = root.querySelector('[data-hrs-row="' + p.key + '"] input[data-size="' + CSS.escape(v.size) + '"]'); if (r) r.checked = true; }
    });
  } catch (e) { /* no preset */ }

  root.addEventListener('change', (e) => {
    const r = e.target;
    if (!r.matches('input[type="radio"][data-piece]')) return;
    state[r.dataset.piece] = r.dataset.size;
    m.clear();
    sync();
  });

  if (btn) btn.addEventListener('click', async () => {
    m.clear();
    const missing = data.pieces.filter((p) => !state[p.key]);
    if (missing.length) {
      missing.forEach((p) => { const row = rowOf(p.key); if (row) row.classList.add('is-need'); rowStatus(p.key, 'Choose a size for the ' + p.short, 'is-need'); });
      m.fail('Choose a size for the ' + missing.map((p) => p.short).join(', the ').replace(/, the ([^,]+)$/, ' and the $1') + ' to add the set.');
      const first = rowOf(missing[0].key) && rowOf(missing[0].key).querySelector('input[type="radio"]:not(:disabled)');
      if (first) first.focus();
      return;
    }
    const out = data.pieces.filter((p) => { const v = variantOf(p.key, state[p.key]); return !(v && v.available); });
    if (out.length) {
      m.fail(out.map((p) => 'The ' + p.title + ' is sold out in ' + state[p.key] + '.').join(' ') + ' Choose another size, or buy the other pieces on their own.');
      return;
    }
    const sv = setVariantFor();
    if (!sv || !sv.available) { m.fail('That combination isn’t available right now. Try another size for one of the pieces.'); return; }
    busyBtn(btn, true);
    try {
      await addItems([{ id: Number(sv.id), quantity: 1 }], root);
      m.done('The complete set is in your bag: ' + data.pieces.map((p) => p.short + ' ' + state[p.key].replace('Men’s ', '')).join(', ') + '.');
    } catch (err) {
      // Shopify names the piece that ran out (shared inventory); say it plainly
      m.fail((err.message || 'The set could not be added.') + ' Your sizes are still selected.');
      refreshAvailability().then(sync);
    } finally { busyBtn(btn, false); }
  });

  // pages can be cached; check live availability once, quietly
  async function refreshAvailability() {
    const root_ = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    const get = async (h) => { const r = await fetch(root_ + 'products/' + h + '.js', { headers: { Accept: 'application/json' } }); if (!r.ok) throw 0; return r.json(); };
    try {
      const res = await Promise.all(data.pieces.map((p) => get(p.handle)).concat([get(data.set.handle)]));
      data.pieces.forEach((p, i) => { const live = res[i]; p.variants.forEach((v) => { const lv = live.variants.find((x) => String(x.id) === String(v.id)); if (lv) v.available = !!lv.available; }); });
      const liveSet = res[res.length - 1];
      data.set.variants.forEach((v) => { const lv = liveSet.variants.find((x) => String(x.id) === String(v.id)); if (lv) v.available = !!lv.available; });
    } catch (e) { /* keep what the page was rendered with */ }
  }
  sync();
  refreshAvailability().then(sync);
}

function boot(scope) {
  (scope || document).querySelectorAll('[data-hrs]').forEach((root) => { if (root.dataset.hrsReady) return; root.dataset.hrsReady = '1'; revealInit(root); });
  (scope || document).querySelectorAll('[data-hrs-card]').forEach((c) => { if (c.dataset.hrsReady) return; c.dataset.hrsReady = '1'; cardInit(c); });
  (scope || document).querySelectorAll('[data-hrs-set]').forEach((s) => { if (s.dataset.hrsReady) return; s.dataset.hrsReady = '1'; setInit(s); });
}
boot(document);
document.addEventListener('shopify:section:load', (e) => boot(e.target));
watchCart();

window.HartReserve = { planMerge, reconcile, addItems, splitSet };
